import React, { Fragment, useEffect, useRef, useState } from "react";
import { faEye, faEyeSlash, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Loader from "react-loader-spinner";
import swal from "sweetalert";
import AuthService from "../../../services/AuthService";
import GoalChange from "./GoalChange";
import ContactCounselor from "./ContactCounselor";
import AppHeader from "../../../Layout/AppHeader";
import AppSidebar from "../../../Layout/AppSidebar";
import { userInfoAction } from "../../../reducers/UserReducer";
import "../../admin/StudentProgress/StudentProgress.css";
import "./SchoolitProfile.css";

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  if (data.mobile?.[0]) return data.mobile[0];
  if (data.recovery_email?.[0]) return data.recovery_email[0];
  if (data.email?.[0]) return data.email[0];
  if (data.password?.[0]) return data.password[0];
  if (data.confirm_password?.[0]) return data.confirm_password[0];
  if (data.first_name?.[0]) return data.first_name[0];
  if (data.last_name?.[0]) return data.last_name[0];
  return (
    data.Message ||
    data.message ||
    data.error_message ||
    data.error ||
    data.detail ||
    "Something went wrong. Please try again."
  );
};

const SchoolitProfile = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const scrollToSuccess = useRef();
  const user = useSelector((state) => state.UserReducer?.userObj);

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [language, setLanguage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const [showError, setShowError] = useState(false);
  const [profileSavedSuccessfully, setProfileSavedSuccessfully] = useState(false);
  const [openGoalChange, setOpenGoalChange] = useState(false);
  const [openContactCounselor, setOpenContactCounselor] = useState(false);

  const isSchoolIt = user?.role === "school_it";

  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setEmail(user.email || "");
    setMobile(user.mobile || "");
    setRecoveryEmail(user.recovery_email || "");
    setLanguage(user.language || "");
  }, [user]);

  const showApiError = (error) => {
    swal({
      text: getApiErrorMessage(error),
      icon: "warning",
      dangerMode: true,
      buttons: { confirm: true },
      closeOnClickOutside: false,
    });
  };

  const onUpdateClick = () => {
    if (confirmPassword && password !== confirmPassword) {
      setShowError(true);
      return;
    }

    if (!firstName || !lastName || !mobile || !email || !recoveryEmail) {
      swal({
        text: "Please fill in all required fields (First Name, Last Name, Mobile, Email & Secondary Email)",
        icon: "warning",
        dangerMode: true,
        buttons: { confirm: true },
        closeOnClickOutside: false,
      });
      return;
    }

    const obj = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      mobile: String(mobile).trim(),
      recovery_email: recoveryEmail.trim().toLowerCase(),
    };

    if (language) obj.language = language;
    if (password) obj.password = password;
    if (confirmPassword) obj.confirm_password = confirmPassword;

    const finalObj = { ...(user || {}), ...obj };
    setLoading(true);

    AuthService.updateProfile(obj)
      .then((data) => {
        setLoading(false);
        const updatedUser = data?.data?.response
          ? { ...(user || {}), ...data.data.response }
          : finalObj;

        localStorage.setItem("user", JSON.stringify(updatedUser));
        dispatch(userInfoAction(updatedUser));
        setPassword("");
        setConfirmPassword("");
        setShowError(false);
        setProfileSavedSuccessfully(true);

        swal({
          text: "Profile updated successfully",
          icon: "success",
          buttons: {
            confirm: {
              text: "OK",
              value: true,
            },
          },
          closeOnClickOutside: false,
        });

        setTimeout(() => setProfileSavedSuccessfully(false), 3500);
        if (scrollToSuccess.current) {
          scrollToSuccess.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        showApiError(error);
      });
  };

  const onCancelClick = () => {
    if (isSchoolIt) {
      history.push("/school_it/student_statistics");
      return;
    }
    history.push("/admin/export");
  };

  return (
    <Fragment>
      <AppHeader />
      <div className="app-main">
        <AppSidebar />
        <div className="app-main__outer">
          <div className="admin-ccplus-page admin-profile-page">
            <div className="admin-ccplus-header">
              <div>
                <h1 className="admin-ccplus-title">Profile Information</h1>
                <p className="admin-ccplus-subtitle">
                  Update your personal details and account password
                </p>
              </div>
              {isSchoolIt && (
                <div className="admin-ccplus-header-actions">
                  <button
                    type="button"
                    onClick={() => setOpenGoalChange(true)}
                    className="admin-ccplus-btn secondary toolbar"
                  >
                    Goal Change
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenContactCounselor(true)}
                    className="admin-ccplus-btn secondary toolbar"
                  >
                    Contact Counselor
                  </button>
                </div>
              )}
            </div>

            {profileSavedSuccessfully && (
              <div
                ref={scrollToSuccess}
                id="success-container"
                className="admin-profile-success"
              >
                Profile updated successfully
              </div>
            )}

            <div className="admin-ccplus-form-card admin-profile-card">
              <div className="admin-profile-section">
                <h3 className="admin-profile-section-title">Personal Information</h3>
                <div className="admin-profile-grid">
                  <div className="admin-course-set-field">
                    <label className="admin-course-set-label" htmlFor="profile-first-name">
                      First Name <span className="admin-course-set-required">*</span>
                    </label>
                    <input
                      id="profile-first-name"
                      name="firstName"
                      className="admin-course-set-input"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div className="admin-course-set-field">
                    <label className="admin-course-set-label" htmlFor="profile-last-name">
                      Last Name <span className="admin-course-set-required">*</span>
                    </label>
                    <input
                      id="profile-last-name"
                      name="lastName"
                      className="admin-course-set-input"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
              </div>

              <div className="admin-profile-section">
                <h3 className="admin-profile-section-title">Contact Information</h3>
                <div className="admin-profile-grid">
                  <div className="admin-course-set-field">
                    <label className="admin-course-set-label" htmlFor="profile-email">
                      Email Address <span className="admin-course-set-required">*</span>
                    </label>
                    <input
                      id="profile-email"
                      name="email"
                      className="admin-course-set-input"
                      type="text"
                      value={email}
                      disabled
                    />
                  </div>
                  <div className="admin-course-set-field">
                    <label className="admin-course-set-label" htmlFor="profile-mobile">
                      Mobile Phone Number <span className="admin-course-set-required">*</span>
                    </label>
                    <input
                      id="profile-mobile"
                      name="mobile"
                      className="admin-course-set-input"
                      type="text"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="Enter mobile number"
                    />
                  </div>
                  <div className="admin-course-set-field admin-profile-grid-span">
                    <label
                      className="admin-course-set-label"
                      htmlFor="profile-recovery-email"
                    >
                      Secondary Email Address{" "}
                      <span className="admin-course-set-required">*</span>
                    </label>
                    <input
                      id="profile-recovery-email"
                      name="recoveryEmail"
                      className="admin-course-set-input"
                      type="text"
                      value={recoveryEmail}
                      onChange={(e) =>
                        setRecoveryEmail(e.target.value.toLowerCase())
                      }
                      placeholder="Enter secondary email"
                    />
                    <p className="admin-profile-hint">
                      Email address used in case account needs to be recovered.
                    </p>
                  </div>
                </div>
              </div>

              <div className="admin-profile-section">
                <h3 className="admin-profile-section-title">Password</h3>
                <div className="admin-profile-grid">
                  <div className="admin-course-set-field">
                    <label className="admin-course-set-label" htmlFor="profile-password">
                      Password
                    </label>
                    <div className="admin-profile-password-wrap">
                      <input
                        id="profile-password"
                        name="password"
                        className="admin-course-set-input"
                        type={passwordShown ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Leave blank to keep current password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="admin-profile-password-toggle"
                        onClick={() => setPasswordShown((prev) => !prev)}
                        aria-label={passwordShown ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon icon={passwordShown ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>
                  <div className="admin-course-set-field">
                    <label
                      className="admin-course-set-label"
                      htmlFor="profile-confirm-password"
                    >
                      Confirm Password
                    </label>
                    <div className="admin-profile-password-wrap">
                      <input
                        id="profile-confirm-password"
                        name="confirmPassword"
                        className="admin-course-set-input"
                        type={confirmPasswordShown ? "text" : "password"}
                        value={confirmPassword}
                        onFocus={() => setShowError(false)}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="admin-profile-password-toggle"
                        onClick={() => setConfirmPasswordShown((prev) => !prev)}
                        aria-label={
                          confirmPasswordShown
                            ? "Hide confirm password"
                            : "Show confirm password"
                        }
                      >
                        <FontAwesomeIcon
                          icon={confirmPasswordShown ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </div>
                </div>
                {showError && (
                  <div className="admin-profile-error">
                    Password and Confirm Password should be the same.
                  </div>
                )}
              </div>

              <div className="admin-ccplus-form-actions">
                <button
                  type="button"
                  onClick={onUpdateClick}
                  className="admin-ccplus-btn primary"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader type="TailSpin" color="#fff" height={18} width={18} />
                  ) : (
                    <FontAwesomeIcon icon={faSave} />
                  )}
                  Update
                </button>
                <button
                  type="button"
                  onClick={onCancelClick}
                  className="admin-ccplus-btn ghost"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {openGoalChange && (
            <GoalChange
              open={openGoalChange}
              setOpenGoalChange={setOpenGoalChange}
            />
          )}
          {openContactCounselor && (
            <ContactCounselor
              open={openContactCounselor}
              setOpenContactConselor={setOpenContactCounselor}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default SchoolitProfile;
